import { z } from 'zod';
import { Id } from '../primitives.js';

/**
 * Контракты расписания: кресла, смены и визиты.
 *
 * Модуль отличается от остальных тем, что здесь бывают настоящие конфликты:
 * кресло в конкретный час занимает кто-то один. Поэтому в схемах есть то, чего
 * нет у других сущностей, — состояние `tentative` и явная пара «начало и конец».
 *
 * Документация модуля: docs/03-modules/05-scheduling.md
 */

export const AppointmentStatus = z.enum([
  /**
   * Записано без связи и сервером ещё не подтверждено. Не технический флаг:
   * администратор видит такую запись штриховкой и знает, что слот не закреплён.
   */
  'tentative',
  'scheduled',
  'confirmed',
  'arrived',
  'in_progress',
  'completed',
  'no_show',
  'cancelled',
]);
export type AppointmentStatus = z.infer<typeof AppointmentStatus>;

/**
 * Зачем пациент пришёл. Влияет на длительность по умолчанию и на то, какие
 * визиты система создаёт сама по готовности работы в лаборатории.
 */
export const AppointmentKind = z.enum([
  'consultation',
  'treatment',
  'try_in',
  'installation',
  'checkup',
  'hygiene',
]);
export type AppointmentKind = z.infer<typeof AppointmentKind>;

export const Chair = z.object({
  id: Id,
  tenantId: Id,
  name: z.string().min(1).max(100),
  isActive: z.boolean().default(true),
  rowVersion: z.number().int().nonnegative(),
});
export type Chair = z.infer<typeof Chair>;

export const CreateChairRequest = z.object({ name: z.string().trim().min(1).max(100) });
export type CreateChairRequest = z.infer<typeof CreateChairRequest>;

/**
 * Переименование кресла и его снятие. Удаления нет: в кресле стоят прошлые
 * визиты, и строка, исчезнувшая из справочника, унесла бы с собой расписание
 * прошлого месяца.
 */
export const UpdateChairRequest = z
  .object({
    name: z.string().trim().min(1).max(100).optional(),
    isActive: z.boolean().optional(),
  })
  .refine((patch) => patch.name !== undefined || patch.isActive !== undefined, {
    message: 'Изменение кресла пустое',
  });
export type UpdateChairRequest = z.infer<typeof UpdateChairRequest>;

/**
 * Час и минуты рабочего дня клиники в её собственном поясе, «ЧЧ:ММ».
 *
 * Не метка времени: «клиника открывается в девять» — это правило, а не событие
 * конкретного дня, и переход на летнее время его не сдвигает. Кратность
 * четверти часа — шаг сетки: граница в 09:07 означала бы столбец, начинающийся
 * посреди слота.
 */
export const ClockTime = z
  .string()
  .regex(/^(?:[01]\d|2[0-3]):(?:00|15|30|45)$/, 'Время задаётся с точностью до четверти часа');
export type ClockTime = z.infer<typeof ClockTime>;

/**
 * Часы приёма клиники — границы сетки расписания.
 *
 * Одна пара на клинику, а не расписание по дням недели: сетка показывает один
 * день, и вопрос, на который отвечают эти два поля, — «докуда рисовать ось».
 * Кто именно работает в конкретный день, знают смены; закрытые дни — закрытия.
 * Разложить часы по дням недели значило бы завести третий источник правды о
 * рабочем времени, который обязан совпадать с двумя первыми.
 */
export const ClinicHours = z.object({
  tenantId: Id,
  opensAt: ClockTime,
  closesAt: ClockTime,
  rowVersion: z.number().int().nonnegative(),
});
export type ClinicHours = z.infer<typeof ClinicHours>;

export const SetClinicHoursRequest = z
  .object({ opensAt: ClockTime, closesAt: ClockTime })
  .refine((hours) => hours.opensAt < hours.closesAt, {
    message: 'Открытие должно быть раньше закрытия',
    path: ['closesAt'],
  });
export type SetClinicHoursRequest = z.infer<typeof SetClinicHoursRequest>;

/**
 * Часы по умолчанию, пока клиника их не задала: тот же день, что рисовала
 * сетка с первого дня продукта. Значение живёт в контрактах, потому что его
 * обязаны знать одинаково и сервер, и устройство без связи.
 */
export const DEFAULT_CLINIC_HOURS: { opensAt: ClockTime; closesAt: ClockTime } = {
  opensAt: '08:00',
  closesAt: '21:00',
};

/**
 * Рабочая смена врача: где и когда он принимает.
 *
 * Хранится развёрнутой по датам, а не правилом повторения. Правило пришлось бы
 * разворачивать и на сервере, и на устройстве без сети, и оба разворота обязаны
 * совпасть до минуты — вместе с отпусками, подменами и переносами праздников.
 * Развёрнутая смена занимает больше места и не умеет ошибаться.
 */
export const WorkShift = z.object({
  id: Id,
  tenantId: Id,
  doctorId: Id,
  chairId: Id,
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  /**
   * Снятая смена уходит из сетки. Строка остаётся, потому что обмен иначе не
   * пришлёт `remove`, и устройство продолжит рисовать врача в этот час.
   */
  isActive: z.boolean().default(true),
  rowVersion: z.number().int().nonnegative(),
});
export type WorkShift = z.infer<typeof WorkShift>;

/**
 * Одна смена на один день. Повторение по дням недели разворачивает клиент:
 * правило пришлось бы считать и на сервере, и на устройстве без сети.
 */
export const CreateWorkShiftRequest = z
  .object({
    doctorId: Id,
    chairId: Id,
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
  })
  .refine((shift) => shift.startsAt < shift.endsAt, {
    message: 'Начало смены должно быть раньше конца',
    path: ['endsAt'],
  });
export type CreateWorkShiftRequest = z.infer<typeof CreateWorkShiftRequest>;

export const UpdateWorkShiftRequest = z
  .object({
    doctorId: Id.optional(),
    chairId: Id.optional(),
    startsAt: z.iso.datetime().optional(),
    endsAt: z.iso.datetime().optional(),
    isActive: z.boolean().optional(),
  })
  .refine(
    (patch) =>
      patch.doctorId !== undefined ||
      patch.chairId !== undefined ||
      patch.startsAt !== undefined ||
      patch.endsAt !== undefined ||
      patch.isActive !== undefined,
    { message: 'Изменение смены пустое' },
  );
export type UpdateWorkShiftRequest = z.infer<typeof UpdateWorkShiftRequest>;

/**
 * Разрезать смену на две в момент `at` — из контекстного меню сетки. Обе
 * половины остаются за тем же врачом и кабинетом и отвязываются от графика.
 * `newShiftId` — клиентский идентификатор второй половины: повтор запроса после
 * потерянного ответа узнаёт по нему уже сделанный разрез.
 */
export const SplitWorkShiftRequest = z.object({
  at: z.iso.datetime(),
  newShiftId: Id,
});
export type SplitWorkShiftRequest = z.infer<typeof SplitWorkShiftRequest>;

const WorkShiftVersion = z.object({ id: Id, rowVersion: z.number().int().nonnegative() });

export const SplitWorkShiftResponse = z.object({
  /** Первая половина — прежняя смена, вторая — новая. */
  shifts: z.tuple([WorkShiftVersion, WorkShiftVersion]),
});
export type SplitWorkShiftResponse = z.infer<typeof SplitWorkShiftResponse>;

/**
 * Передать смену другому врачу — из контекстного меню сетки. С
 * `moveAppointments` вместе со сменой к нему переходят и приёмы прежнего врача в
 * этом кабинете за время смены; без — приёмы остаются за прежним.
 */
export const ReassignWorkShiftRequest = z.object({
  doctorId: Id,
  moveAppointments: z.boolean(),
});
export type ReassignWorkShiftRequest = z.infer<typeof ReassignWorkShiftRequest>;

export const ReassignWorkShiftResponse = z.object({
  id: Id,
  rowVersion: z.number().int().nonnegative(),
  /** Сколько приёмов перешло к новому врачу. */
  movedAppointments: z.number().int().nonnegative(),
});
export type ReassignWorkShiftResponse = z.infer<typeof ReassignWorkShiftResponse>;

/** День недели по ISO: 1 — понедельник, 7 — воскресенье. */
export const Weekday = z.number().int().min(1).max(7);
export type Weekday = z.infer<typeof Weekday>;

/**
 * График врача: правило, из которого берутся смены.
 *
 * Смена по-прежнему хранится развёрнутой по датам — правило рядом с ней не
 * второй источник правды, а её источник: смены на ближайшие недели заводит из
 * графика сервер, и разворот существует ровно в одном месте. Устройство без
 * сети по-прежнему читает готовые смены, а не считает правило.
 *
 * Пустой `endsOn` — постоянная основа: у графика нет последнего дня, и сервер
 * продлевает смены сам, по мере того как горизонт уходит вперёд.
 */
export const WorkPattern = z.object({
  id: Id,
  tenantId: Id,
  doctorId: Id,
  chairId: Id,
  /** Дни недели, в которые врач принимает. Пустой список — график ни о чём. */
  weekdays: z.array(Weekday).min(1).max(7),
  opensAt: ClockTime,
  closesAt: ClockTime,
  startsOn: z.iso.date(),
  /** Последний день графика. Пусто — постоянная основа, без конца. */
  endsOn: z.iso.date().nullable(),
  /**
   * Снятый график больше не заводит смен, а уже заведённые будущие снимает.
   * Строка остаётся: по ней видно, кто и по какому расписанию принимал.
   */
  isActive: z.boolean().default(true),
  /**
   * Удалён из списка. `.default(null)` — строки кэша, приехавшие до этого
   * поля. Снятый график (`isActive: false`) ещё возвращается; удалённый нет.
   */
  deletedAt: z.iso.datetime().nullable().default(null),
  rowVersion: z.number().int().nonnegative(),
});
export type WorkPattern = z.infer<typeof WorkPattern>;

export const CreateWorkPatternRequest = z
  .object({
    doctorId: Id,
    chairId: Id,
    weekdays: z.array(Weekday).min(1).max(7),
    opensAt: ClockTime,
    closesAt: ClockTime,
    startsOn: z.iso.date(),
    endsOn: z.iso.date().nullable().optional(),
  })
  .refine((pattern) => pattern.opensAt < pattern.closesAt, {
    message: 'Начало приёма должно быть раньше конца',
    path: ['closesAt'],
  })
  .refine(
    (pattern) => pattern.endsOn === undefined || pattern.endsOn === null || pattern.startsOn <= pattern.endsOn,
    { message: 'Последний день графика не раньше первого', path: ['endsOn'] },
  );
export type CreateWorkPatternRequest = z.infer<typeof CreateWorkPatternRequest>;

export const UpdateWorkPatternRequest = z
  .object({
    doctorId: Id.optional(),
    chairId: Id.optional(),
    weekdays: z.array(Weekday).min(1).max(7).optional(),
    opensAt: ClockTime.optional(),
    closesAt: ClockTime.optional(),
    startsOn: z.iso.date().optional(),
    endsOn: z.iso.date().nullable().optional(),
    isActive: z.boolean().optional(),
    /** Убрать график из списка. Прошлые смены остаются, будущие снимаются. */
    deleted: z.literal(true).optional(),
  })
  .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
    message: 'Изменение графика пустое',
  });
export type UpdateWorkPatternRequest = z.infer<typeof UpdateWorkPatternRequest>;

/** Праздник, ремонт, санитарный день: время, когда не работает вся клиника. */
export const ClinicClosure = z.object({
  id: Id,
  tenantId: Id,
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  reason: z.string().min(1).max(200),
  rowVersion: z.number().int().nonnegative(),
});
export type ClinicClosure = z.infer<typeof ClinicClosure>;

/** Заметка вместо пациента: слот занят, в сетке светло-красный. */
export const ChairHold = z.object({
  id: Id,
  tenantId: Id,
  chairId: Id,
  doctorId: Id.nullable(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  comment: z.string().min(1).max(300),
  rowVersion: z.number().int().nonnegative(),
});
export type ChairHold = z.infer<typeof ChairHold>;

export const CreateChairHoldRequest = z.object({
  chairId: Id,
  doctorId: Id.nullable().optional(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  comment: z.string().trim().min(1).max(300),
});
export type CreateChairHoldRequest = z.infer<typeof CreateChairHoldRequest>;

/** Время и заметка окна. Кресло не меняется: в другое кресло окно ставят заново. */
export const UpdateChairHoldRequest = z
  .object({
    startsAt: z.iso.datetime().optional(),
    endsAt: z.iso.datetime().optional(),
    comment: z.string().trim().min(1).max(300).optional(),
  })
  .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
    message: 'Изменение окна пустое',
  });
export type UpdateChairHoldRequest = z.infer<typeof UpdateChairHoldRequest>;

export const Appointment = z.object({
  id: Id,
  tenantId: Id,
  patientId: Id,
  doctorId: Id,
  chairId: Id,
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  status: AppointmentStatus,
  kind: AppointmentKind,
  /** С чем пришёл пациент. Видно в сетке и в карте. */
  reason: z.string().max(500).nullable(),
  cancelReason: z.string().max(500).nullable(),
  /**
   * Наряд лаборатории, к которому относится визит (примерка, установка).
   * Ссылка мягкая (см. `packages/contracts/src/modules/lab-orders.ts`) —
   * существование наряда и его принадлежность клинике проверяет
   * application-слой, а не внешний ключ в базе.
   */
  labOrderId: Id.nullable(),
  /**
   * Когда запись сделали на устройстве. Отличается от `createdAt`, если писали
   * без связи, и по нему видно, сколько запись пролежала в очереди.
   */
  clientCreatedAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
  /**
   * ФИО пациента — подпись визита в сетке. Обмен кладёт её в каждый визит:
   * визиты едут на устройство на 120 дней вперёд, а карта пациента — только
   * на неделю, и без подписи визит через месяц читался бы «Пациент не
   * загружен». Карта, если она на устройстве, главнее: имя в ней свежее.
   * Пусто — у визита, заведённого на устройстве, и у строк кэша до этого поля.
   */
  patientName: z.string().max(400).nullable().optional(),
});
export type Appointment = z.infer<typeof Appointment>;

/**
 * Создание визита.
 *
 * Идентификатор задаёт клиент — как и во всех записях, которые делаются без
 * сети. Статус в запросе не передаётся: его определяет сервер, и только он
 * решает, стала ли предварительная запись подтверждённой.
 */
export const CreateAppointmentRequest = z.object({
  patientId: Id,
  doctorId: Id,
  chairId: Id,
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  kind: AppointmentKind,
  reason: z.string().max(500).optional(),
});
export type CreateAppointmentRequest = z.infer<typeof CreateAppointmentRequest>;

/**
 * Перенос визита. Меняются только время, кресло и врач: всё остальное правится
 * как обычная карточка, а перенос — это спор за дефицитный ресурс, и он ходит
 * своим путём с проверкой занятости.
 */
export const RescheduleAppointmentRequest = z.object({
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  chairId: Id.optional(),
  doctorId: Id.optional(),
  /** Версия строки, которую видел пользователь: защита от чужой правки. */
  expectedRowVersion: z.number().int().nonnegative().optional(),
});
export type RescheduleAppointmentRequest = z.infer<typeof RescheduleAppointmentRequest>;

/**
 * Смена статуса. Отдельный путь, а не поле в правке: переход проверяется
 * автоматом состояний, и «поставить completed» — это не то же самое, что
 * записать значение в колонку.
 */
/**
 * Привязка визита к наряду лаборатории — или снятие связи (`labOrderId: null`).
 * Отдельный вид изменения, а не поле в переносе: связь с нарядом не спорит за
 * ресурс и не проверяется автоматом статусов, ей не место ни в одном из двух
 * существующих видов.
 */
export const LinkLabOrderRequest = z.object({
  labOrderId: Id.nullable(),
});
export type LinkLabOrderRequest = z.infer<typeof LinkLabOrderRequest>;

export const ChangeAppointmentStatusRequest = z.object({
  to: AppointmentStatus,
  /** Обязательна при отмене: в карте должно быть видно, почему визит не состоялся. */
  reason: z.string().max(500).optional(),
});
export type ChangeAppointmentStatusRequest = z.infer<typeof ChangeAppointmentStatusRequest>;

/**
 * Изменение визита в очереди устройства.
 *
 * У протокола обмена всего две операции — создать и изменить, — а изменений у
 * визита два разных: перенос и смена статуса. Различать их по форме тела
 * («есть время — значит перенос») можно, но такое различение молча ломается от
 * первого же необязательного поля. Поэтому вид изменения назван явно.
 */
export const AppointmentChange = z.discriminatedUnion('change', [
  RescheduleAppointmentRequest.extend({ change: z.literal('move') }),
  ChangeAppointmentStatusRequest.extend({ change: z.literal('status') }),
  LinkLabOrderRequest.extend({ change: z.literal('lab-order') }),
]);
export type AppointmentChange = z.infer<typeof AppointmentChange>;

/**
 * Переход статуса как отдельная запись.
 *
 * История неизменяема: по ней разбирают, кто и когда отметил неявку, и
 * переписать её задним числом нельзя.
 */
export const AppointmentStatusChange = z.object({
  id: Id,
  tenantId: Id,
  appointmentId: Id,
  from: AppointmentStatus.nullable(),
  to: AppointmentStatus,
  reason: z.string().max(500).nullable(),
  changedBy: Id,
  changedAt: z.iso.datetime(),
});
export type AppointmentStatusChange = z.infer<typeof AppointmentStatusChange>;

/**
 * Чем кончился звонок пациенту накануне визита.
 *
 * `no_answer` — тоже исход, а не пропуск: «не дозвонились в 18:40» говорит
 * следующему администратору, что звонить надо ещё раз, и не раньше чем через
 * час. Подтверждение, отмена и перенос меняют сам визит своими путями (статус,
 * перенос), а звонок сохраняет то, чего в визите нет, — что сказал пациент.
 */
export const APPOINTMENT_CALL_OUTCOME = ['confirmed', 'cancelled', 'rescheduled', 'no_answer'] as const;
export const AppointmentCallOutcome = z.enum(APPOINTMENT_CALL_OUTCOME);
export type AppointmentCallOutcome = z.infer<typeof AppointmentCallOutcome>;

/**
 * Звонок пациенту по визиту — строка обзвона на завтра и запись в карте
 * пациента. Append-only, как история переходов: что пациент сказал по
 * телефону, задним числом не переписывают.
 *
 * Пациента в строке нет: он берётся из визита. Карты пациентов сливают, и
 * визит при этом переезжает к выжившей карте вместе со своими звонками —
 * копия идентификатора в неизменяемой строке осталась бы у удалённой.
 */
export const AppointmentCall = z.object({
  id: Id,
  tenantId: Id,
  appointmentId: Id,
  outcome: AppointmentCallOutcome,
  /** Обязателен: «подтвердил» без слов пациента не отличить от нажатой по ошибке кнопки. */
  comment: z.string().min(1).max(500),
  /**
   * Когда стоял визит в момент звонка. Перенесённый визит потом стоит в другом
   * дне, а звонок был о прежнем — по этому полю его находит обзвон того дня.
   */
  visitStartsAt: z.iso.datetime(),
  calledBy: Id,
  /** Когда звонили — время на устройстве: очередь без связи уходит позже разговора. */
  calledAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});
export type AppointmentCall = z.infer<typeof AppointmentCall>;

/**
 * Запись звонка. Идентификатор выдаёт клиент, как и всем записям без сети;
 * автора и время сервер берёт из сессии и конверта операции.
 */
export const RecordAppointmentCallRequest = z.object({
  appointmentId: Id,
  outcome: AppointmentCallOutcome,
  comment: z.string().trim().min(1, 'Напишите, что сказал пациент').max(500),
  visitStartsAt: z.iso.datetime(),
});
export type RecordAppointmentCallRequest = z.infer<typeof RecordAppointmentCallRequest>;

/** Свободное время: ответ на поиск и он же — список альтернатив при отказе. */
export const FreeSlot = z.object({
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  chairId: Id,
  doctorId: Id,
});
export type FreeSlot = z.infer<typeof FreeSlot>;

export const FreeSlotsQuery = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  durationMinutes: z.coerce.number().int().positive().max(480),
  doctorId: Id.optional(),
  chairId: Id.optional(),
  /** Какой визит переносим: его собственное время не считается занятым. */
  excludeAppointmentId: Id.optional(),
  limit: z.coerce.number().int().positive().max(50).default(20),
});
export type FreeSlotsQuery = z.infer<typeof FreeSlotsQuery>;

export const FreeSlots = z.object({
  slots: z.array(FreeSlot),
});
export type FreeSlots = z.infer<typeof FreeSlots>;

/**
 * Лист ожидания: пациент, которому в сетке не нашлось времени.
 *
 * Заявка не удаляется, а закрывается: записался — `booked` с номером визита,
 * передумал — `cancelled` с причиной. По закрытым видно, чем очередь
 * оборачивается на самом деле, — а список, из которого строки исчезают,
 * отвечает только на вопрос «кто ждёт сейчас».
 */
export const WaitlistState = z.enum(['waiting', 'booked', 'cancelled']);
export type WaitlistState = z.infer<typeof WaitlistState>;

export const WaitlistEntry = z.object({
  id: Id,
  tenantId: Id,
  patientId: Id,
  /** Пусто — «к любому свободному врачу»: самая ценная заявка в очереди. */
  doctorId: Id.nullable(),
  kind: AppointmentKind,
  /** Сколько времени нужно. Кратно шагу сетки, иначе визит в неё не встанет. */
  durationMinutes: z.number().int().positive().max(480),
  /** Раньше этого пациент прийти не может. Пусто — хоть сейчас. */
  wantedFrom: z.iso.datetime().nullable(),
  wantedTo: z.iso.datetime().nullable(),
  /** Что сказал пациент: «могу подъехать за 15 минут», «только после 18». */
  note: z.string().max(500).nullable(),
  status: WaitlistState,
  /** Визит, которым закрылась заявка. Есть только у `booked`. */
  appointmentId: Id.nullable(),
  /** Почему сняли с очереди. Есть только у `cancelled`. */
  closeReason: z.string().max(500).nullable(),
  clientCreatedAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
});
export type WaitlistEntry = z.infer<typeof WaitlistEntry>;

export const CreateWaitlistEntryRequest = z.object({
  patientId: Id,
  doctorId: Id.optional(),
  kind: AppointmentKind,
  durationMinutes: z.number().int().positive().max(480),
  wantedFrom: z.iso.datetime().optional(),
  wantedTo: z.iso.datetime().optional(),
  note: z.string().max(500).optional(),
});
export type CreateWaitlistEntryRequest = z.infer<typeof CreateWaitlistEntryRequest>;

/**
 * Снятие с очереди. Приходит операцией `soft_delete` — физического удаления в
 * системе нет, — а различает два исхода поле `outcome`: записали пациента или
 * он больше не ждёт. Без различения очередь показывала бы одинаково и работу
 * регистратуры, и потерянных пациентов.
 */
export const CloseWaitlistEntryRequest = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('booked'), appointmentId: Id }),
  z.object({ outcome: z.literal('cancelled'), reason: z.string().min(1).max(500) }),
]);
export type CloseWaitlistEntryRequest = z.infer<typeof CloseWaitlistEntryRequest>;

export const WaitlistQuery = z.object({
  /** По умолчанию — только ждущие: закрытые заявки нужны отчёту, а не стойке. */
  status: WaitlistState.default('waiting'),
  patientId: Id.optional(),
});
export type WaitlistQuery = z.infer<typeof WaitlistQuery>;

export const Waitlist = z.object({
  entries: z.array(WaitlistEntry),
});
export type Waitlist = z.infer<typeof Waitlist>;

export const ScheduleQuery = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  chairId: Id.optional(),
  doctorId: Id.optional(),
});
export type ScheduleQuery = z.infer<typeof ScheduleQuery>;

/**
 * Сетка расписания за период: сами визиты плюс то, из чего складывается фон, —
 * кресла, смены и закрытия. Без фона нельзя отличить свободное время от
 * нерабочего, а «пусто» и «клиника закрыта» — разные экраны.
 */
export const Schedule = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  chairs: z.array(Chair),
  shifts: z.array(WorkShift),
  closures: z.array(ClinicClosure),
  appointments: z.array(Appointment),
});
export type Schedule = z.infer<typeof Schedule>;
